import { create } from 'zustand';
import { databaseService } from '@/services/DatabaseService';
import { userProfileService } from '@/services/UserProfileService';

// Define subscription status types
export type SubscriptionPlan = 'free' | 'plus';
export type SubscriptionStatus = 'active' | 'cancelled' | 'expired' | 'trial';

// Define subscription store interface
interface SubscriptionState {
  plan: SubscriptionPlan;
  status: SubscriptionStatus;
  expiresAt: string | null;
  features: string[];
  hasPlus: boolean;
  isLoading: boolean;
  error: string | null;
  
  fetchSubscription: () => Promise<void>;
  setSubscription: (plan: SubscriptionPlan, status: SubscriptionStatus, expiresAt: string | null) => void;
}

/**
 * Subscription Store
 * Manages the user's subscription state and Premium Plus status
 */
export const useSubscriptionStore = create<SubscriptionState>((set, get) => ({
  // Initialize with free plan and no Premium Plus access by default
  plan: 'free',
  status: 'active',
  expiresAt: null,
  features: ['basic'],
  hasPlus: false,
  isLoading: false,
  error: null,
  
  // Fetch subscription information from the database
  fetchSubscription: async () => {
    try {
      set({ isLoading: true });
      
      // Get user profile to check subscription
      const userProfile = await userProfileService.getCurrentProfile();
      
      if (!userProfile) {
        set({
          plan: 'free',
          status: 'active',
          expiresAt: null,
          features: ['basic'],
          hasPlus: false,
          isLoading: false
        });
        return;
      }
      
      // Check if user has Plus subscription
      const hasActivePlus = userProfile.subscription?.plan === 'plus' && 
                          userProfile.subscription?.status === 'active';
      
      const plusFeatures = [
        'analytics',
        'export',
        'unlimited_profiles',
        'caregiver_access',
        'advanced_reminders'
      ];
      
      set({
        plan: userProfile.subscription?.plan || 'free',
        status: userProfile.subscription?.status || 'active',
        expiresAt: userProfile.subscription?.expiresAt || null,
        features: hasActivePlus ? ['basic', ...plusFeatures] : ['basic'],
        hasPlus: hasActivePlus,
        isLoading: false
      });
      
    } catch (error) {
      
      set({ 
        error: 'Failed to fetch subscription status', 
        isLoading: false,
        plan: 'free',
        hasPlus: false
      });
    }
  },
  
  // Set subscription data
  setSubscription: (plan, status, expiresAt) => {
    const hasActivePlus = plan === 'plus' && status === 'active';
    const plusFeatures = [
      'analytics',
      'export',
      'unlimited_profiles',
      'caregiver_access',
      'advanced_reminders'
    ];
    
    set({
      plan,
      status,
      expiresAt,
      features: hasActivePlus ? ['basic', ...plusFeatures] : ['basic'],
      hasPlus: hasActivePlus,
      isLoading: false
    });
  }
}));
